import React from 'react'
import {
  OneEmptyState,
  ResourceHeader,
  Spinner,
  Stack,
} from '@factorialco/f0-react/dist/experimental'
import { F0Dialog } from '@factorialco/f0-react'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import {
  TicketDetailDocument,
  UpdateTicketDocument,
  TicketsConnectionDocument,
} from 'generated/resources/graphql'
import { TicketAttachments } from 'modules/ticketing/components/TicketDetailPanel/TicketAttachments'
import { TicketComments } from 'modules/ticketing/components/TicketDetailPanel/TicketComments'
import { TicketDetailList } from 'modules/ticketing/components/TicketDetailPanel/TicketDetailList'
import app from 'modules/core/store/models/app'

export type TicketDetailPanelProps = {
  ticketId: string
  onClose: () => void
}

export const TicketDetailPanel: React.FC<TicketDetailPanelProps> = ({ ticketId, onClose }) => {
  const {
    data,
    loading,
    error,
    refetch: refetchTicket,
  } = useQuery(TicketDetailDocument, {
    variables: { id: ticketId },
    fetchPolicy: 'network-only',
  })

  const [performUpdate] = useMutation(UpdateTicketDocument, {
    extractorFn: (result) => result.ticketingMutations.updateTicketingTicket,
    refetchQueries: [TicketsConnectionDocument],
  })

  const ticket = data?.ticketing?.ticket ?? null

  const handleFieldUpdate = async (variables: Record<string, unknown>) => {
    if (!ticket) return

    const result = await performUpdate({
      variables: {
        id: ticket.id,
        ...variables,
      },
    })

    const handled = handleResult(result, () => {})
    if (handled.success && handled.data?.ticket != null) {
      refetchTicket()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('ticketing.inbox.detail.update_error'),
      })
    }
  }

  const handleStatusChange = (newStatus: string) => handleFieldUpdate({ status: newStatus })

  const handlePriorityChange = (newPriority: string) => handleFieldUpdate({ priority: newPriority })

  const handleAssigneeChange = (fields: {
    assigneeTeamId?: string
    assigneeMemberId?: string | null
  }) => handleFieldUpdate(fields)

  const notFound = !loading && ticket == null && error == null
  const showError = Boolean(error)

  if (showError) {
    return (
      <Stack paddingX="p-4" paddingY="p-4" gap="4" data-testid="ticket-detail-panel">
        <OneEmptyState
          title={i18n.t('ticketing.inbox.error_state_title')}
          description={i18n.t('ticketing.inbox.error_state_description')}
          variant="critical"
        />
      </Stack>
    )
  }

  if (notFound) {
    return (
      <Stack paddingX="p-4" paddingY="p-4" gap="4" data-testid="ticket-detail-panel">
        <OneEmptyState
          title={i18n.t('ticketing.inbox.detail.not_found')}
          description={i18n.t('ticketing.inbox.detail.not_found_description')}
          variant="critical"
        />
      </Stack>
    )
  }

  if (!ticket) return null

  return (
    <F0Dialog
      isOpen
      position="right"
      module={{
        id: 'inbox',
        label: i18n.t('ticketing.sidebar.inbox'),
      }}
      onClose={onClose}
    >
      {loading && !ticket ? (
        <Stack
          paddingX="p-4"
          paddingY="p-4"
          gap="4"
          alignItems="center"
          data-testid="ticket-detail-panel"
        >
          <Spinner size="large" />
        </Stack>
      ) : (
        <Stack paddingX="p-4" paddingY="p-4" gap="4" data-testid="ticket-detail-panel">
          <ResourceHeader title={ticket.title} metadata={[]} />
          <TicketDetailList
            ticket={ticket}
            onStatusChange={handleStatusChange}
            onPriorityChange={handlePriorityChange}
            onAssigneeChange={handleAssigneeChange}
          />
          <TicketAttachments ticket={ticket} onAttachmentAdded={() => refetchTicket()} />
          <TicketComments ticketId={ticket.id} />
        </Stack>
      )}
    </F0Dialog>
  )
}
